import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NeoDialogModule, NeoDialogRef, NEO_DIALOG_DATA } from '@neogov/pattern-library/neo-dialog';
import { ToastProvider } from '@core';
import { InputFieldComponent } from '../input-field/input-field.component';
import { SwitchComponent } from '../switch/switch.component';
import { ButtonComponent } from '../button/button.component';
import { IconComponent } from '../icon/icon.component';
import {
  CreateSharepointConfigurationUseCase,
  UpdateSharepointConfigurationUseCase,
  GetSharepointConfigurationsStateUseCase,
  CreateSharepointConfigurationRequestDto,
  UpdateSharepointConfigurationRequestDto,
} from '@features/sharepoint-configuration';

export interface SharepointConfigurationFlyoutData {
  isEditMode: boolean;
}

export interface SharepointConfigurationFlyoutResult {
  saved: boolean;
}

/**
 * SharePoint Configuration Flyout Component
 * Flyout for configuring Microsoft SharePoint integration.
 */
@Component({
  selector: 'cm-sharepoint-configuration-flyout',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NeoDialogModule,
    InputFieldComponent,
    SwitchComponent,
    ButtonComponent,
    IconComponent,
  ],
  templateUrl: './sharepoint-configuration-flyout.component.html',
  styleUrl: './sharepoint-configuration-flyout.component.less',
})
export class SharepointConfigurationFlyoutComponent implements OnInit {
  private readonly _flyoutRef: NeoDialogRef<SharepointConfigurationFlyoutComponent, SharepointConfigurationFlyoutResult> = inject(NeoDialogRef);
  private readonly _data: SharepointConfigurationFlyoutData = inject(NEO_DIALOG_DATA);
  private readonly _fb = inject(FormBuilder);
  private readonly _createUseCase = inject(CreateSharepointConfigurationUseCase);
  private readonly _updateUseCase = inject(UpdateSharepointConfigurationUseCase);
  private readonly _getState = inject(GetSharepointConfigurationsStateUseCase);
  private readonly _toast: ToastProvider = inject(ToastProvider);

  public readonly state = this._getState.execute();
  public form!: FormGroup;
  public isEditMode = false;

  public ngOnInit(): void {
    this.isEditMode = this._data.isEditMode;
    this._initializeForm();
  }

  public onCancel(): void {
    this._flyoutRef.close();
  }

  public onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue = this.form.value;

    if (this.isEditMode) {
      this._updateConfiguration(formValue);
    } else {
      this._createConfiguration(formValue);
    }
  }

  private _initializeForm(): void {
    const config = this.state.sharepointConfiguration();

    this.form = this._fb.group({
      sharepointTenantId: [config?.sharepointTenantId || '', [Validators.required, Validators.maxLength(50)]],
      clientId: [config?.clientId || '', [Validators.required, Validators.maxLength(50)]],
      clientSecret: [config?.clientSecret || '', Validators.required],
      baseUrl: [config?.baseUrl || '', [Validators.required, Validators.maxLength(500)]],
      automaticFolderCreation: [config?.automaticFolderCreation || false],
    });
  }

  private _createConfiguration(formValue: CreateSharepointConfigurationRequestDto): void {
    this._createUseCase.execute(formValue).subscribe({
      next: () => {
        this._toast.success('SharePoint configuration created successfully');
        this._flyoutRef.close({ saved: true });
      },
      error: (error: { error?: { message?: string } }) => {
        this._toast.error(error?.error?.message || 'Failed to create SharePoint configuration');
      },
    });
  }

  private _updateConfiguration(formValue: UpdateSharepointConfigurationRequestDto): void {
    this._updateUseCase.execute(formValue).subscribe({
      next: () => {
        this._toast.success('SharePoint configuration updated successfully');
        this._flyoutRef.close({ saved: true });
      },
      error: (error: { error?: { message?: string } }) => {
        this._toast.error(error?.error?.message || 'Failed to update SharePoint configuration');
      },
    });
  }
}
